import React, { useState, useEffect } from 'react';
import { FiUserPlus, FiEye, FiEyeOff, FiCopy, FiChevronLeft, FiChevronRight, FiCheck, FiPlus, FiList } from 'react-icons/fi';
import '../../styles/Admin/LiveAccount.css';
import { getCurrentUser, getUserMT5Accounts } from '../../lib/supabase/helpers';
import { supabase } from '../../lib/supabase/client';
import { api } from '../../services/api';

// ─── STATIC groups — edit to add/remove groups ─────────────────────────────
const STATIC_GROUPS = {
  'FOREX': [
    'IND\\3001\\FOREX\\36000\\STANDARD-contest',
    // 'IND\\3001\\FOREX\\36000\\STANDARD',
    // 'IND\\3001\\FOREX\\36000\\PRO',
  ],
  'COMEX': [
    // 'IND\\3001\\COMEX\\28001\\STANDARD',
    // 'IND\\3001\\COMEX\\28001\\PRO',
  ],
  'LOT': [
    // 'IND\\3001\\LOT\\28001\\STANDARD',
    // 'IND\\3001\\LOT\\28001\\PRO',
  ],
};

// ─── The ONE static demo account — always shown first, always DEMO ──────────
const STATIC_DEMO_ACCOUNT = {
  id: 'demo-280102',
  login_id: '280102',
  password: 'Meta@9988',
  account_type: 'demo',
  account_group: 'IND\\3001\\FOREX\\36000\\STANDARD-contest',
  is_active: true,
  currency: 'USD',
  leverage: 500,
  balance: 10000,
  equity: 10000,
  margin: 10000,
  free_margin: 10000,
  server_name: 'Bulge Group Investment Limited.'
};

function LiveAccount() {
  const [showPassword, setShowPassword]               = useState({});
  const [currentAccountIndex, setCurrentAccountIndex] = useState(0);
  const [loading, setLoading]                         = useState(true);
  const [creating, setCreating]                       = useState(false);
  const [currentUser, setCurrentUser]                 = useState(null);
  const [accounts, setAccounts]                       = useState([]);
  const [activeTab, setActiveTab]                     = useState('accounts');
  const [copiedField, setCopiedField]                 = useState(null);

  const [formData, setFormData] = useState({
    accountGroup: 'IND\\3001\\FOREX\\36000\\STANDARD',
    leverage: 500
  });

  useEffect(() => { fetchAccounts(); }, []);

  // ─── fetchAccounts: ALWAYS prepend static demo, then live MT5 accounts ──
  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const user = await getCurrentUser();
      if (!user) { setLoading(false); return; }
      setCurrentUser(user);

      const mt5Accounts = await getUserMT5Accounts(user.user.id);

      // Static demo is ALWAYS first — live accounts appended after
      const liveAccounts = (mt5Accounts || []).filter(
        acc => acc.login_id !== '280102'   // avoid duplicate if somehow in DB
      );
      setAccounts([STATIC_DEMO_ACCOUNT, ...liveAccounts]);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAccounts([STATIC_DEMO_ACCOUNT]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'leverage' ? parseInt(value) : value }));
  };

  // ─── handleSubmit — original MT5 creation logic, completely untouched ───
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) { alert('Please login first'); return; }
    if (!formData.accountGroup) { alert('Please select an account group'); return; }

    setCreating(true);
    try {
      const userName =
        currentUser.user?.user_metadata?.full_name ||
        currentUser.user?.email?.split('@')[0] ||
        'User';
      const userEmail = currentUser.user?.email || '';

      const result = await api.createAccount({
        name: userName,
        email: userEmail,
        group: formData.accountGroup,
        leverage: formData.leverage,
        initialBalance: 0
      });

      if (result.success) {
        const accountData = result.data;

        let accountGroupEnum = 'LIVE PRO';
        if (accountData.group.includes('demo') || accountData.group.includes('DEMO')) {
          accountGroupEnum = 'DEMO';
        } else if (accountData.group.includes('STANDARD')) {
          accountGroupEnum = 'LIVE STANDARD';
        } else if (accountData.group.includes('PRO')) {
          accountGroupEnum = 'LIVE PRO';
        }

        console.log('Saving to Supabase:', {
          user_id: currentUser.user.id,
          login_id: accountData.login.toString(),
          group: accountData.group,
          mapped_enum: accountGroupEnum
        });

        const { data: insertedData, error: saveError } = await supabase
          .from('mt5_accounts')
          .insert([{
            user_id: currentUser.user.id,
            login_id: accountData.login.toString(),
            password: accountData.trading_password,
            account_group: accountData.group,
            leverage: accountData.leverage,
            balance: 0,
            equity: 0,
            margin: 0,
            free_margin: 0,
            margin_level: 0
          }])
          .select();

        if (saveError) {
          console.error('Supabase save error:', saveError);
          alert(`Warning: Account created in MT5 but failed to save to database: ${saveError.message}`);
        } else {
          console.log('Saved to Supabase:', insertedData);
        }

        alert(
          `Account Created Successfully!\n` +
          `Login: ${accountData.login}\n` +
          `Trading Password: ${accountData.trading_password}\n` +
          `Investor Password: ${accountData.investor_password}\n\n` +
          `Please save these credentials!`
        );

        await fetchAccounts();
        setFormData(prev => ({ ...prev, leverage: 500 }));
        setActiveTab('accounts');
      } else {
        alert('Failed to create account. Please try again.');
      }
    } catch (error) {
      console.error('Error creating account:', error);
      alert(`Failed to create account: ${error.message || 'Please try again.'}`);
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const togglePasswordVisibility = (accountId) => {
    setShowPassword(prev => ({ ...prev, [accountId]: !prev[accountId] }));
  };

  const nextAccount = () => { if (currentAccountIndex < accounts.length - 1) setCurrentAccountIndex(i => i + 1); };
  const prevAccount = () => { if (currentAccountIndex > 0) setCurrentAccountIndex(i => i - 1); };

  const removeOneSlash = (str) => str.replace(/\\\\/g, '\\');
  const getLastPart    = (str) => { if (!str) return ''; return str.split('\\').pop(); };

  // Demo = ONLY login_id '280102' (the static demo account)
  // Every MT5 account from Supabase is LIVE regardless of group name
  const isAccountDemo = (account) => account?.login_id === '280102';

  if (loading) {
    return (
      <div className="la-page">
        <div className="la-loader">
          <div className="la-spinner" />
          <p>Loading your accounts...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="la-page">
        <div className="la-empty-state">
          <div className="la-empty-icon">🔐</div>
          <h2>Please log in to manage accounts</h2>
          <button className="la-btn-primary" onClick={() => window.location.href = '/login'}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const currentAccount = accounts[currentAccountIndex];

  return (
    <div className="la-page">
      <div className="la-wrapper">

        {/* ── Page Header ── */}
        <div className="la-page-header">
          <div className="la-page-title">
            <h1>Trading Accounts</h1>
            <p>Manage and create your MT5 trading accounts</p>
          </div>
          <div className="la-tab-toggle">
            <button
              className={`la-tab-btn ${activeTab === 'accounts' ? 'active' : ''}`}
              onClick={() => setActiveTab('accounts')}
            >
              <FiList size={14} /> My Accounts
              {accounts.length > 0 && <span className="la-tab-badge">{accounts.length}</span>}
            </button>
            <button
              className={`la-tab-btn ${activeTab === 'create' ? 'active' : ''}`}
              onClick={() => setActiveTab('create')}
            >
              <FiPlus size={14} /> New Account
            </button>
          </div>
        </div>

        {/* ════════ ACCOUNTS TAB ════════ */}
        {activeTab === 'accounts' && (
          <div className="la-accounts-view">
            {accounts.length === 0 ? (
              <div className="la-empty-state">
                <div className="la-empty-icon">📊</div>
                <h3>No accounts yet</h3>
                <p>Create your first trading account to get started.</p>
                <button className="la-btn-primary" onClick={() => setActiveTab('create')}>
                  <FiPlus size={14} /> Create Account
                </button>
              </div>
            ) : (
              <>
                {/* Selector pills */}
                <div className="la-account-pills">
                  {accounts.map((acc, idx) => (
                    <button
                      key={acc.id}
                      className={`la-pill ${idx === currentAccountIndex ? 'active' : ''}`}
                      onClick={() => setCurrentAccountIndex(idx)}
                    >
                      <span className={`la-pill-dot ${isAccountDemo(acc) ? 'demo' : 'live'}`} />
                      #{acc.login_id}
                      <span className={`la-pill-tag ${isAccountDemo(acc) ? 'demo' : 'live'}`}>
                        {isAccountDemo(acc) ? 'Demo' : 'Live'}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Account Card */}
                <div className="la-account-card">

                  <div className="la-card-header">
                    <div className="la-card-header-left">
                      <div className={`la-type-badge ${isAccountDemo(currentAccount) ? 'demo' : 'live'}`}>
                        {isAccountDemo(currentAccount) ? 'DEMO' : 'LIVE'}
                      </div>
                      <div>
                        <div className="la-login-num">{currentAccount.login_id}</div>
                        <div className="la-group-label">{getLastPart(currentAccount.account_group)}</div>
                      </div>
                    </div>
                    <div className={`la-status-chip ${currentAccount.is_active ? 'on' : 'off'}`}>
                      <span className="la-status-dot" />
                      {currentAccount.is_active ? 'Enabled' : 'Disabled'}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="la-stats-row">
                    {[
                      { label: 'Balance',     value: `$${parseFloat(currentAccount.balance     || 0).toFixed(2)}` },
                      { label: 'Equity',      value: `$${parseFloat(currentAccount.equity      || 0).toFixed(2)}` },
                      { label: 'Margin',      value: `$${parseFloat(currentAccount.margin      || 0).toFixed(2)}` },
                      { label: 'Free Margin', value: `$${parseFloat(currentAccount.free_margin || 0).toFixed(2)}` },
                    ].map(s => (
                      <div className="la-stat" key={s.label}>
                        <span className="la-stat-label">{s.label}</span>
                        <span className="la-stat-value">{s.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Credentials */}
                  <div className="la-creds">
                    <div className="la-creds-title">Login Credentials</div>

                    <div className="la-cred-row">
                      <span className="la-cred-label">Terminal Login</span>
                      <div className="la-cred-right">
                        <span className="la-cred-val mono">{currentAccount.login_id}</span>
                        <button
                          className={`la-icon-btn ${copiedField === `login-${currentAccount.id}` ? 'done' : ''}`}
                          onClick={() => handleCopy(currentAccount.login_id, `login-${currentAccount.id}`)}
                        >
                          {copiedField === `login-${currentAccount.id}` ? <FiCheck size={13} /> : <FiCopy size={13} />}
                        </button>
                      </div>
                    </div>

                    <div className="la-cred-row">
                      <span className="la-cred-label">Main Password</span>
                      <div className="la-cred-right">
                        <span className="la-cred-val mono">
                          {showPassword[currentAccount.id]
                            ? (currentAccount.password || '—')
                            : '••••••••••'}
                        </span>
                        <button
                          className="la-icon-btn"
                          onClick={() => togglePasswordVisibility(currentAccount.id)}
                        >
                          {showPassword[currentAccount.id] ? <FiEyeOff size={13} /> : <FiEye size={13} />}
                        </button>
                        {currentAccount.password && (
                          <button
                            className={`la-icon-btn ${copiedField === `pw-${currentAccount.id}` ? 'done' : ''}`}
                            onClick={() => handleCopy(currentAccount.password, `pw-${currentAccount.id}`)}
                          >
                            {copiedField === `pw-${currentAccount.id}` ? <FiCheck size={13} /> : <FiCopy size={13} />}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="la-cred-row">
                      <span className="la-cred-label">Server Name</span>
                      <div className="la-cred-right">
                        <span className="la-cred-val">Bulge Group Investment Limited.</span>
                        <button
                          className={`la-icon-btn ${copiedField === 'server' ? 'done' : ''}`}
                          onClick={() => handleCopy('Bulge Group Investment Limited.', 'server')}
                        >
                          {copiedField === 'server' ? <FiCheck size={13} /> : <FiCopy size={13} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Meta strip */}
                  <div className="la-meta-strip">
                    <div className="la-meta-item">
                      <span className="la-meta-label">Group</span>
                      <span className="la-meta-val">{getLastPart(currentAccount.account_group)}</span>
                    </div>
                    <div className="la-meta-item">
                      <span className="la-meta-label">Currency</span>
                      <span className="la-meta-val">{currentAccount.currency || 'USD'}</span>
                    </div>
                    <div className="la-meta-item">
                      <span className="la-meta-label">Leverage</span>
                      <span className="la-meta-val">1:{currentAccount.leverage}</span>
                    </div>
                    <div className="la-meta-item">
                      <span className="la-meta-label">Type</span>
                      <span className="la-meta-val">{isAccountDemo(currentAccount) ? 'Demo' : 'Real'}</span>
                    </div>
                  </div>
                </div>

                {/* Carousel nav */}
                {accounts.length > 1 && (
                  <div className="la-carousel-nav">
                    <button className="la-nav-btn" onClick={prevAccount} disabled={currentAccountIndex === 0}>
                      <FiChevronLeft />
                    </button>
                    <span className="la-nav-label">{currentAccountIndex + 1} / {accounts.length}</span>
                    <button className="la-nav-btn" onClick={nextAccount} disabled={currentAccountIndex === accounts.length - 1}>
                      <FiChevronRight />
                    </button>
                  </div>
                )}

                <button className="la-add-link" onClick={() => setActiveTab('create')}>
                  <FiPlus size={13} /> Add another account
                </button>
              </>
            )}
          </div>
        )}

        {/* ════════ CREATE TAB ════════ */}
        {activeTab === 'create' && (
          <div className="la-create-view">
            <div className="la-create-card">
              <div className="la-create-header">
                <div className="la-create-icon-wrap">
                  <FiUserPlus size={22} />
                </div>
                <div>
                  <h2>Create Live Account</h2>
                  <p>Fill in the details below to open a new trading account.</p>
                </div>
              </div>

              <form className="la-form" onSubmit={handleSubmit}>

                {/* Account Group — 100% static */}
                <div className="la-form-group">
                  <label className="la-label">Account Group</label>
                  <select
                    className="la-select"
                    name="accountGroup"
                    value={formData.accountGroup}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Account Group --</option>
                    {Object.keys(STATIC_GROUPS).map((category) => (
                      <optgroup key={category} label={category}>
                        {STATIC_GROUPS[category].map((groupName) => (
                          <option key={groupName} value={removeOneSlash(groupName)}>
                            {getLastPart(groupName)}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                {/* Leverage */}
                <div className="la-form-group">
                  <label className="la-label">Leverage</label>
                  <div className="la-leverage-grid">
                    {[100, 200, 500, 1000].map(lev => (
                      <button
                        key={lev}
                        type="button"
                        className={`la-lev-btn ${formData.leverage === lev ? 'active' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, leverage: lev }))}
                      >
                        1:{lev}
                      </button>
                    ))}
                  </div>
                </div>

                <button type="submit" className="la-btn-submit" disabled={creating}>
                  {creating
                    ? <><div className="la-spinner small white" /> Creating Account...</>
                    : <><FiUserPlus size={15} /> Create Account</>}
                </button>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default LiveAccount;